/**
 * Inicialización del shell nativo (Capacitor).
 *
 * Cuando la app corre dentro del wrapper nativo (Android / iOS), este módulo
 * configura status bar, splash screen y push notifications nativas, y
 * sincroniza el token con el backend (`/api/push/register-native`).
 *
 * En el navegador / PWA estos calls son no-ops gracias al guard `isNative`.
 */
let appInitialized = false;

async function setupNativePush(api) {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.checkPermissions();
    let status = perm.receive;
    if (status === "prompt") {
      const req = await PushNotifications.requestPermissions();
      status = req.receive;
    }
    if (status !== "granted") return;

    await PushNotifications.register();

    PushNotifications.addListener("registration", async (token) => {
      // token.value es el FCM/APNs token
      try {
        await api.post("/push/register-native", {
          token: token.value,
          platform: window?.Capacitor?.getPlatform?.() || "unknown",
        });
      } catch (e) {
        /* offline-safe */
      }
    });

    PushNotifications.addListener("registrationError", (err) => {
      // eslint-disable-next-line no-console
      console.warn("[capacitor] push registration error", err);
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const url = action?.notification?.data?.url;
      if (url && typeof url === "string") {
        window.location.href = url.startsWith("http") ? url : url;
      }
    });
  } catch (e) {
    /* plugin not available — running in browser */
  }
}

async function setupStatusBar() {
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: "#0A0A0A" });
  } catch {
    /* not native */
  }
}

async function hideSplash() {
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch {
    /* not native */
  }
}

/**
 * Punto de entrada. Se invoca una vez en `index.js`.
 * @param {object} api - cliente axios para registrar el token de push.
 */
export async function initCapacitor(api) {
  if (appInitialized) return;
  const isNative = typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.();
  if (!isNative) return;
  appInitialized = true;
  await setupStatusBar();
  await hideSplash();
  if (api) await setupNativePush(api);
}

export default initCapacitor;
